/**
 * Prueba de micrófono — obligatoria antes del primer ejercicio (master §1
 * regla 1). Sin señal de voz verificada, una batería entera puede terminar en
 * `inconcluyente` por cobertura de audio: es más barato fallar aquí.
 *
 * El resultado se registra en la batería (`POST /baterias/:id/mic-check`) desde
 * el hook; esta pantalla solo mide y avisa.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Mascot, SpeechBubble } from "../components/Mascot.js";
import { Button } from "../components/Button.js";
import { useT } from "../i18n/index.js";
import { medirNivel, siguienteEstado, ESTADO_VOZ_INICIAL, UMBRAL_VOZ } from "./vad.js";
import styles from "./EvaluationPlayer.module.css";

type Fase = "idle" | "midiendo" | "ok" | "fallo";

const DURACION_MS = 2_500;
const FRAME_MS = 100;

export function MicCheck({ onOk }: { onOk: () => void }) {
  const t = useT();
  const [fase, setFase] = useState<Fase>("idle");
  const [nivel, setNivel] = useState(0);
  const limpiar = useRef<() => void>(() => undefined);

  useEffect(() => () => limpiar.current(), []);

  const probar = useCallback(async () => {
    setFase("midiendo");
    setNivel(0);
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setFase("fallo");
      return;
    }
    const Ctx = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
    if (!Ctx) {
      // Sin WebAudio no podemos medir: el permiso concedido es la única señal.
      stream.getTracks().forEach((x) => x.stop());
      setFase("ok");
      return;
    }
    const ctx = new Ctx();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(analyser);

    let estado = ESTADO_VOZ_INICIAL;
    const cerrar = () => {
      window.clearInterval(timer);
      void ctx.close();
      stream?.getTracks().forEach((x) => x.stop());
    };
    limpiar.current = cerrar;
    const timer = window.setInterval(() => {
      const n = medirNivel(analyser);
      setNivel((prev) => Math.max(prev * 0.7, n));
      estado = siguienteEstado(estado, n, FRAME_MS);
      if (estado.msTotal >= DURACION_MS) {
        cerrar();
        limpiar.current = () => undefined;
        setFase(estado.vozDetectada ? "ok" : "fallo");
      }
    }, FRAME_MS);
  }, []);

  const pct = Math.min(100, Math.round((nivel / (UMBRAL_VOZ * 4)) * 100));

  return (
    <section className={styles.kid}>
      <div className={styles.body}>
        <SpeechBubble>{t("eval.peff.mic.body")}</SpeechBubble>
        <Mascot size={110} />

        <div className={styles.micMeter} aria-label={t("eval.peff.mic.title")}>
          <div className={styles.micFill} style={{ width: `${pct}%` }} />
        </div>

        {fase === "ok" ? <p className={styles.micOk}>{t("eval.peff.mic.ok")}</p> : null}
        {fase === "fallo" ? <p className={styles.micFail}>{t("eval.peff.mic.fail")}</p> : null}

        <Button variant="primary" onClick={() => void probar()} disabled={fase === "midiendo"}>
          {fase === "midiendo" ? t("eval.peff.mic.testing") : t("eval.peff.mic.test")}
        </Button>

        {/* El check se registra igual tras un intento fallido: el adulto puede
            decidir seguir (p. ej. auricular sin medición fiable), pero nunca se
            entra al primer ejercicio sin haber pasado por esta pantalla. */}
        {fase === "ok" || fase === "fallo" ? (
          <Button variant="sun" onClick={onOk}>
            {t("eval.peff.mic.start")}
          </Button>
        ) : null}
      </div>
    </section>
  );
}
